import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import groupBy from 'lodash.groupby';
import maxBy from 'lodash.maxby';
import { CONTENT_TYPE, HISTORY_PERIOD } from '../../enums';
import coxLogo from '../../public/images/cox-logo.png';
import { logout } from '../../service/Auth.service';
import { mapError } from '../../utils/alerts/error';
import { handleError } from '../../service/base.service';
import { abortOngoingGenerationStatusChecks } from '../../service/ContentGeneration';
import { getHistoryList, getPromptHistory } from '../../service/History.service';
import { formatDate, getCurrentDate, getOneDayBeforeDate } from '../../utils/date';
import { redirectUnauthorized } from '../../utils/errors';
import Alert from '../Alert/Alert';
import Drawer from '../Drawer/Drawer';
import StatusIcon from '../StatusIcon/StatusIcon';
import { FEATURE_FLAGS, isFeatureEnabled } from '../../utils/featureFlag';
import { get, remove, set, STORAGE_KEYS } from '../../utils/storage';
import { isSameDay, isSameMonth } from '../../utils/date';

// Custom hook to safely use MSAL
const useMsalSafe = () => {
  try {
    return useMsal();
  } catch (e) {
    // Return null when not in MSAL context
    return { instance: null, accounts: [], inProgress: 'none' };
  }
};

const MENU_REFRESH_RATE_MS = 10000;
const THRESHOLD = 15;
const INITIAL_PAGE = 1;
const LIMIT_ITEMS_PER_PAGE = 100;

export const historyGroupsDisplayOrder = [
  HISTORY_PERIOD.Today,
  HISTORY_PERIOD.Yesterday,
  HISTORY_PERIOD.ThisMonth,
  HISTORY_PERIOD.Older
];

const groupHistoryListByPeriods = (historyList) => {
  const today = getCurrentDate();
  const yesterday = getOneDayBeforeDate(today);

  const getDatesPeriodRepresentation = ({ createdAt }) => {
    const date = new Date(createdAt);

    if (isSameDay(date, today)) {
      return HISTORY_PERIOD.Today;
    }

    if (isSameDay(date, yesterday)) {
      return HISTORY_PERIOD.Yesterday;
    }

    if (isSameMonth(date, today)) {
      return HISTORY_PERIOD.ThisMonth;
    }

    return HISTORY_PERIOD.Older;
  };

  return groupBy(historyList, getDatesPeriodRepresentation);
};

export const updateHistoryList = (currentHistoryList, response) => {
  const sortBy = (key) => (a, b) => (a[key] > b[key] ? -1 : b[key] > a[key] ? 1 : 0);

  const groupedData = groupBy([...response.data, ...currentHistoryList], 'contentRequestId');
  const updateEntries = Object.entries(groupedData).map(([_, value]) =>
    maxBy(value, 'lastRequestTryNumber')
  );

  return updateEntries.sort(sortBy('sequentialIndex'));
};

const Shell = ({ children, header = null, isDrawerOpen: initialDrawerState = false }) => {
  const [isDrawerOpen, setDrawerState] = useState(initialDrawerState);
  const [userBasedHistory, setUserBasedHistory] = useState(
    get(STORAGE_KEYS.USER_CONFIGURATION)?.userBasedHistory ?? true
  );
  const [promptHistory, setPromptHistory] = useState([]);
  const [historyList, setHistoryList] = useState([]);
  const [page, setPage] = useState(INITIAL_PAGE);
  const [error, setError] = useState(null);
  const itemRef = useRef(null);
  const requestState = useRef({
    firstCall: true
  });
  
  // Use safe MSAL hook that won't crash if not in MSAL context
  const { instance } = useMsalSafe();
  
  const navigate = useNavigate();
  const { id } = useParams();
  const hasError = error !== null;

  const handleNewRequest = () => {
    setDrawerState(false);

    abortOngoingGenerationStatusChecks();
    navigate('/', { replace: true });
  };

  const handleHistory = async ({ contentRequestId, lastRequestTryNumber }) => {
    try {
      const response = await getPromptHistory({
        contentRequestId,
        lastRequestTryNumber
      });

      abortOngoingGenerationStatusChecks(contentRequestId, lastRequestTryNumber);
      setDrawerState(false);
      navigate(`/prompt/${contentRequestId}/${lastRequestTryNumber}`, {
        state: response
      });
    } catch (e) {
      handleError(e, {
        UnauthorizedError: () => redirectUnauthorized(navigate),
        default: () => setError(mapError(e.name, e.message))
      });
    }
  };

  const handleLogout = async () => {
    try {
      if (isFeatureEnabled(FEATURE_FLAGS.ENTRA)) {
        await instance?.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
      } else {
        remove(STORAGE_KEYS.USER_CONFIGURATION);
        await logout();
        navigate(0, { replace: true });
      }
    } catch (e) {
      handleError(e, {
        default: () => setError(mapError(e.name, e.message))
      });
    }
  };

  useEffect(() => {
    let intervalId;
    if (isDrawerOpen) {
      const fetchHistory = async () => {
        try {
          requestState.current.isNextPageLoading = true;

          const response = await getHistoryList(
            userBasedHistory,
            LIMIT_ITEMS_PER_PAGE,
            INITIAL_PAGE
          );

          requestState.current = {
            ...requestState.current,
            firstCall: false,
            isNextPageLoading: false,
            page: response.page,
            limit: response.limit,
            total: response.total
          };
          setHistoryList((currrentHistoryList) => updateHistoryList(currrentHistoryList, response));
          setError(null);
        } catch (e) {
          handleError(e, {
            UnauthorizedError: () => redirectUnauthorized(navigate),
            default: () => setError(mapError(e.name, e.message))
          });
          requestState.current.isNextPageLoading = false;
        }
      };

      fetchHistory();

      intervalId = setInterval(() => {
        if (!requestState.current.isNextPageLoading) {
          fetchHistory();
        }
      }, MENU_REFRESH_RATE_MS);
    } else {
      clearInterval(intervalId);
    }

    return () => {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, navigate, userBasedHistory]);

  useEffect(() => {
    set(STORAGE_KEYS.USER_CONFIGURATION, {
      userBasedHistory
    });

    requestState.current = { ...requestState.current, firstCall: true };
    setHistoryList([]);
    setPage(INITIAL_PAGE);
  }, [userBasedHistory]);

  useEffect(() => {
    setPromptHistory(groupHistoryListByPeriods(historyList));
  }, [historyList]);

  const handleNewPageLoad = async (userBasedHistory, pageLimit, nextPage) => {
    if (requestState.current.isNextPageLoading || historyList.length > requestState.current.total) {
      return;
    }

    try {
      requestState.current.isNextPageLoading = true;

      const response = await getHistoryList(userBasedHistory, pageLimit, nextPage);
      setHistoryList((currrentHistoryList) => updateHistoryList(currrentHistoryList, response));
      requestState.current = {
        ...requestState.current,
        isNextPageLoading: false,
        page: response.page,
        total: response.total
      };
      setPage(response.page);
      setError(null);
      itemRef.current = null;
    } catch (e) {
      requestState.current.isNextPageLoading = false;
      handleError(e, {
        UnauthorizedError: () => redirectUnauthorized(navigate),
        default: () => setError(mapError(e.name, e.message))
      });
    }
  };

  const callbackFn = (entries) => {
    if (entries.at(-1)?.isIntersecting && !requestState.current.isNextPageLoading) {
      handleNewPageLoad(userBasedHistory, requestState.current.limit, page + 1);
    }
  };

  const options = {
    root: null,
    rootMargin: '0px',
    threshold: 1.0
  };

  useEffect(() => {
    const observer = new IntersectionObserver(callbackFn, options);

    if (itemRef.current) {
      observer.observe(itemRef.current);
    }

    return () => {
      if (itemRef.current) {
        observer.unobserve(itemRef.current);
        observer.disconnect();
      }
    };
  }, [itemRef, options]);

  const renderError = (error) => (
    <div className="mb-4" data-testid="error-alert">
      <Alert level="error" label={error.label}>
        {error.details}
      </Alert>
    </div>
  );

  const renderMainInfo = (prompt, dateMode) => {
    const { contentType, contentRequestId, generatedContentStatus, createdAt, sequentialIndex } =
      prompt ?? {};

    return (
      <div className="flex justify-between items-center">
        <div className="flex justify-between items-center">
          <StatusIcon
            dataTest={`${contentType}-${contentRequestId}-status`}
            status={generatedContentStatus}
            size="small"
            className="align-bottom"
          />
          <div
            className="text-sm leading-4 ml-1"
            data-testid={`${contentType}-${contentRequestId}-label`}
          >
            {`${sequentialIndex}. ${
              CONTENT_TYPE[contentType.toUpperCase()].shortLabel ??
              CONTENT_TYPE[contentType.toUpperCase()].label
            }`}
          </div>
        </div>
        <div
          className="text-xs font-normal leading-4"
          data-testid={`${contentType}-${contentRequestId}-date`}
        >
          {formatDate(new Date(createdAt), dateMode)}
        </div>
      </div>
    );
  };

  const renderDealershipAccountId = (prompt) => {
    const { dealershipAccountId } = prompt ?? {};

    return dealershipAccountId ? (
      <div className="mt-1 text-sm font-normal leading-5">
        Dealership ID: <span className="font-bold">{dealershipAccountId}</span>
      </div>
    ) : null;
  };

  const renderLink = (prompt, dateMode, { index, arr }) => {
    const { contentRequestId, contentType, unretryable } = prompt ?? {};
    const active = contentRequestId === id;
    const key = `${contentRequestId}-list`;
    const testId = `${contentType}-${contentRequestId}${unretryable ? '-unretryable' : ''}`;
    const className = [
      'cursor-pointer py-2 last:pb-0 mb-1 last:mb-0',
      ...(active ? ['font-bold'] : []),
      ...(unretryable ? ['text-grey-300 line-through'] : [])
    ].join(' ');

    return (
      <li
        key={key}
        onClick={() => !unretryable && handleHistory(prompt)}
        data-testid={testId}
        className={className}
        ref={index === arr.length - THRESHOLD ? itemRef : null}
      >
        {renderMainInfo(prompt, dateMode)}
        {renderDealershipAccountId(prompt)}
      </li>
    );
  };

  const renderHistoryGroup = (groupName, groupItems) => {
    const dateMode = groupName === HISTORY_PERIOD.Today ? 'time_only' : 'date_only';
    const links = groupItems.map((previousPrompt, index, arr) =>
      renderLink(previousPrompt, dateMode, { index, arr })
    );

    return (
      <div
        key={`link-group-${groupName}`}
        className="border-greyborder-grey-200 border-b border-solid py-2"
      >
        <h3 className="text-sm font-semibold leading-4">{groupName}</h3>
        <ul className="no-tw">{links}</ul>
      </div>
    );
  };

  const renderHistory = (promptHistory) =>
    historyGroupsDisplayOrder
      .filter((groupName) => promptHistory[groupName]?.length > 0)
      .map((groupName) => renderHistoryGroup(groupName, promptHistory[groupName]));

  return (
    <div
      key="shell-wrapper"
      data-testid="shell-wrapper"
      className="h-screen w-screen overflow-x-hidden flex"
    >
      <aside>
        <Drawer
          isOpen={isDrawerOpen}
          setIsOpen={setDrawerState}
          handleNewRequest={handleNewRequest}
          handleLogout={handleLogout}
          userBasedHistory={userBasedHistory}
          setUserBasedHistory={setUserBasedHistory}
        >
          {hasError && renderError(error)}

          <div data-testid="shell-drawer-content">{renderHistory(promptHistory)}</div>
        </Drawer>
      </aside>
      <div className="ml-16 w-screen">
        <header
          key="shell-header"
          className="bg-white py-4 pl-4 mb-8 shadow-sm relative z-20 flex justify-between items-center"
        >
          <img className="ml-6" src={coxLogo} alt="Cox Automotive Logo" width={104} height={32} />
          <div className="">{header}</div>
        </header>
        <main key="shell-main" data-testid="shell-content" className="mx-8 h-shell-content">
          {children}
        </main>
      </div>
    </div>
  );
};

Shell.propTypes = {
  children: PropTypes.node.isRequired,
  header: PropTypes.node,
  isDrawerOpen: PropTypes.bool
};

export default Shell;
